import * as PIXI from 'pixi.js';
import { Config } from '../../video-data/config';
import { TextWordMetrics } from './TextWordMetrics';
import type { WordStyle } from './WordStyle';

/**
 * Represents measurements for a single line of text. It corresponds to the
 * {@link Luminary.TextLine} container.
 *
 */
export class TextLineMetrics {
    classType = 'TextLineMetrics';
    #textWordList: TextWordMetrics[] = [];
    #removedLeadingSpace = false;
    #removedTrailingSpace = false;

    /**
     * Adds a text word metrics object to the internal list at the specified index.
     * Appends to the list if no index is given.
     *
     * @param textWordMetrics - the new metric data to add
     * @param index - index at which to insert the metric
     */
    insertTextWordMetrics(textWordMetrics: TextWordMetrics, index?: number) {
        if (!(textWordMetrics instanceof TextWordMetrics)) {
            throw new Error('Only TextWordMetrics objects can be added to TextLineMetrics');
        }
        if (index === undefined) {
            index = this.#textWordList.length;
        }
        this.#textWordList.splice(index, 0, textWordMetrics);
    }

    /**
     * The advance width of the line. This includes spaces but not newline characters.
     */
    get advanceWidth(): number {
        return this.#textWordList.reduce((acc, textWord) => acc + textWord.advanceWidth, 0);
    }

    /**
     * The horizontal distance from the origin pen position to the left bounding box edge.
     */
    get bearingX(): number {
        if (this.numTextWords === 0) {
            return 0;
        }
        // metrics assume left aligned text
        return this.#textWordList[0].bearingX;
    }

    /**
     * The average x-height of all text words. It uses the average since different fonts could
     * be used throughout the words.
     */
    get xHeight(): number {
        const sum = this.#textWordList.reduce((acc, textWord) => acc + textWord.xHeight, 0);
        return sum / this.numTextWords;
    }

    /**
     * The vertical distance from the baseline to the highest outline marking of the line.
     * It is a non-negative number.
     */
    get ascent(): number {
        return this.#textWordList.reduce((max, textWord) => Math.max(max, textWord.ascent), 0);
    }


    /**
     * The vertical distance from the baseline to the lowest outline marking of the line.
     * It is a negative number with a maximum of zero.
     */
    get descent(): number {
        return this.#textWordList.reduce((min, textWord) => Math.min(min, textWord.descent), 0);
    }

    /**
     * The extent of the text outside of the x-height area. It is the maximum of:
     *  1. the distance from the top of the x-height to the top of the ascent
     *  2. the magnitude of the descent
     */
    get extentBeyondXHeight(): number {
        const top = this.ascent - this.xHeight;
        const bottom = Math.abs(this.descent);
        return Math.max(top, bottom);
    }

    /**
     * The number of text words in the line.
     */
    get numTextWords(): number {
        return this.#textWordList.length;
    }

    /**
     * Retrieves the internal list of text word metrics.
     */
    get textWords(): TextWordMetrics[] {
        return this.#textWordList;
    }

    /**
     * The number of characters in the line (note this does not include removed leading or trailing
     * spaces)
     */
    get numChars(): number {
        return this.#textWordList.reduce((acc, textWord) => acc + textWord.numChars, 0);
    }

    /**
     * The text in the line as a single string.
     */
    get text(): string {
        return this.#textWordList.reduce((acc, textWord) => acc + textWord.text, '');
    }

    /**
     * If a leading space was removed from the line.
     */
    get removedLeadingSpace(): boolean {
        return this.#removedLeadingSpace;
    }

    /**
     * If a trailing space was removed from the line.
     */
    get removedTrailingSpace(): boolean {
        return this.#removedTrailingSpace;
    }

    /**
     * The x-position of the text line's bounding box.
     */
    get x(): number {
        return this.#textWordList.reduce((min, textWord) => Math.min(min, textWord.x), Infinity);
    }

    /**
     * The y-position of the text line's bounding box.
     */
    get y(): number {
        return this.#textWordList.reduce((min, textWord) => Math.min(min, textWord.y), Infinity);
    }

    /**
     * The style used in generating the metrics. Note it could either return
     * the fill or highlight style.
     */
    get style(): WordStyle {
        if (this.numTextWords === 0) {
            throw new Error('Cannot get text line style, no text words exist');
        }
        return this.#textWordList[0].style;
    }

    /**
     * The width of the line's bounding box.
     */
    get lineWidth(): number {
        return this.getBounds().width;
    }

    /**
     * The height from the bottom of line's descent to the top of the line's ascent.
     */
    get fontHeight(): number {
        return this.ascent - this.descent;
    }

    /**
     * The bounding box which tightly fits the entire line.
     */
    getBounds(): PIXI.Rectangle {
        if (this.numTextWords === 0) {
            return PIXI.Rectangle.EMPTY;
        }
        const box = this.#textWordList[0].getBounds();
        for (let i = 1; i < this.numTextWords; i++) {
            const bounds = this.#textWordList[i].getBounds();
            if (bounds.width > 0) {
                box.enlarge(bounds);
            }
        }
        return box;
    }

    /**
     * Removes and returns the last text word from the line.
     */
    pop(): TextWordMetrics {
        return this.#textWordList.pop();
    }

    /**
     * Set the position of the line by positioning each of the underlying Text Words.
     *
     * @param advanceOriginX - minimum x position of the text line (x-origin of the
     *                                  advance width)
     * @param yBaseline - the y position of the text line's baseline.
     */
    setPosition(advanceOriginX = 0, yBaseline = 0) {
        let offsetX = 0;
        this.#textWordList.forEach(textWord => {
            const textWordX = offsetX + advanceOriginX;
            textWord.setPosition(textWordX, yBaseline);
            offsetX += textWord.advanceWidth;
        });
    }

    /**
     * After the initial left-to-right and left aligned position has been set, this modifies the
     * positions to account for the desired reading direction and horizontal alignment.
     *
     * @param maxLineWidth - the max width out of all lines that this line belongs to
     */
    setAlignmentAndReadingDirection(maxLineWidth: number) {
        let lineOffsetX = 0;
        const lineWidth = this.lineWidth;
        this.#textWordList.forEach(textWord => {
            // Handle horizontal alignment
            if (this.style.align === Config.TEXT_ALIGN_HORIZONTAL.RIGHT) {
                lineOffsetX = maxLineWidth - lineWidth;
            }
            else if (this.style.align === Config.TEXT_ALIGN_HORIZONTAL.CENTER) {
                lineOffsetX = (maxLineWidth - lineWidth) / 2;
            }
            else if (this.style.align === Config.TEXT_ALIGN_HORIZONTAL.LEFT) {
                lineOffsetX = 0;
            }

            // If reading RTL then the offset is from the end of the line
            if (this.style.readingDirection === Config.READING_DIRECTION.RTL) {
                lineOffsetX += lineWidth;
            }

            textWord.setAlignmentAndReadingDirection(lineOffsetX);
        });
    }

    /**
     * Removes up to one leading and one trailing space from the line.
     * "space" words can sometimes be left at the beginning or end of a line, leading to weird
     * effects such as unexpected padding or offsets.  This might be unexpected because we wrapped
     * the line or a word was broken.  Any *single* "space" words at the beginning or end of a line
     * should be removed and tracked so we can adjust highlights accordingly.  Only a single
     * "space" should be removed so if the user intentionally (for whatever reason) added
     * multiple spaces we don't remove them all
     */
    removeLeadingTrailingSpaces() {
        if (this.#textWordList.length < 1) {
            return;
        }

        const isLeftAligned = this.style.align === Config.TEXT_ALIGN_HORIZONTAL.LEFT;

        // remove a leading space if it hasn't already been removed
        const firstWord = this.#textWordList[0];
        const isValidTextEditState = isLeftAligned;
        if (isValidTextEditState
            && PIXI.TextMetrics.isBreakingSpace(firstWord.text)
            && !this.#removedLeadingSpace
        ) {
            firstWord.resetWordElementMetrics();
            this.#removedLeadingSpace = true;
        }
    }
}
